import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { Check, X, Clock, Download } from "lucide-react";

export default function StudentOffers() {
  const [offers, setOffers] = useState([]);
  const [msg, setMsg] = useState("");
  const [responding, setResponding] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = () => api.get("/offers").then(r => setOffers(r.data));
  useEffect(() => { load(); }, []);

  const respond = async (oid, decision, note) => {
    try { await api.post(`/offers/${oid}/respond`, { decision, note: note || "" }); setMsg(`Offer ${decision}`); setResponding(null); load(); }
    catch (e) { setMsg(formatError(e)); }
  };

  const downloadPdf = async (oid, title) => {
    if (downloading === oid) return;
    setDownloading(oid);
    try {
      // Fetch as blob so browsers can't inline-render (some inline HTML PDFs
      // trip Chrome's PDF viewer sandbox); force a Save-As via <a download>.
      const r = await api.get(`/offers/${oid}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (title || "offer").replace(/[^A-Za-z0-9 -]/g, "").replace(/\s+/g, "_").slice(0, 40);
      a.download = `OfferLetter_${safe}_${oid.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2500);
    } catch (e) {
      setMsg(formatError(e));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="student-offers">
      <div className="card card-pad">
        <span className="eyebrow">Your offers</span>
        <h2 className="font-display font-black text-3xl mt-1">Inbox</h2>
        <p className="text-muted text-sm mt-1">Review, accept or decline offers from hiring teams.</p>
      </div>
      {offers.length === 0 && <div className="card card-pad text-muted">No offers yet.</div>}
      {offers.map(o => (
        <div key={o.id} className="card card-pad" data-testid={`offer-${o.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span className="eyebrow">{o.from_email}</span>
              <h3 className="font-display font-bold text-xl mt-1">{o.title}</h3>
              <div className="text-sm text-muted mt-1 flex flex-wrap gap-3">
                {o.stipend && <span>Stipend: {o.stipend}</span>}
                {o.salary && <span>Salary: {o.salary}</span>}
                {o.duration && <span>Duration: {o.duration}</span>}
                {o.location && <span>{o.location}</span>}
                {o.start_date && <span>Start: {o.start_date}</span>}
              </div>
            </div>
            <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
              o.status === "accepted" ? "bg-green-100 text-green-800" :
              o.status === "declined" ? "bg-red-100 text-red-800" :
              "bg-blue-100 text-blue-800"
            }`} data-testid={`offer-status-${o.id}`}>{o.status}</span>
          </div>
          {o.body && <p className="text-sm whitespace-pre-wrap mt-3 text-muted">{o.body}</p>}
          {o.expires_at && o.status === "sent" && (
            <div className="text-xs text-yellow-700 mt-2 flex items-center gap-1.5"><Clock size={12}/> Respond by {o.expires_at}</div>
          )}
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => downloadPdf(o.id, o.title)}
              disabled={downloading === o.id}
              data-testid={`download-offer-pdf-${o.id}`}
            >
              <Download size={14}/> {downloading === o.id ? "Preparing…" : "Download PDF"}
            </button>
            {o.status === "sent" && (
              responding === o.id ? (
                <RespondForm oid={o.id} onSubmit={respond} onCancel={() => setResponding(null)} />
              ) : (
                <>
                  <button className="btn-primary" onClick={() => setResponding(o.id)} data-testid={`accept-${o.id}`}><Check size={14}/> Accept</button>
                  <button className="btn-ghost" onClick={() => respond(o.id, "declined", "")} data-testid={`decline-${o.id}`}><X size={14}/> Decline</button>
                </>
              )
            )}
          </div>
        </div>
      ))}
      <p className="text-xs text-muted" data-testid="offer-msg">{msg}</p>
    </div>
  );
}

function RespondForm({ oid, onSubmit, onCancel }) {
  const [note, setNote] = useState("");
  return (
    <div className="mt-3 grid gap-2 w-full">
      <textarea className="input" placeholder="Optional note to the hiring team" value={note} onChange={e=>setNote(e.target.value)}/>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={() => onSubmit(oid, "accepted", note)} data-testid={`accept-confirm-${oid}`}><Check size={14}/> Confirm Accept</button>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
